import { createRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { DragDropContext, DragDropContextProps, Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, IconEdit, IconExternalLink, IconTrash, Input, List, Wrapper } from '@screentone/core';

import PortalDropdown from 'components/portal-dropdown/PortalDropdown';
import { SnippetyResponse, useSnippetyQuery } from 'hooks/useSnippetyQuery';
import { reportError } from 'utils/errorBoundary';
import { decodeHtmlEntities } from 'utils/text';
import { formatBulletLink, isValidURL } from 'utils/url';
import styles from './BulletInput.module.scss';

interface BulletInputProps {
  currentBullets: string[];
  displayNewBulletInput: boolean;
  onDisplayNewBulletInput: (displayNewBulletInput: boolean) => void;
  onExecuteUpdate: (bullets: string[]) => void;
  viewOnly?: boolean;
}

type BulletItem = { id: string; value: string };

const toBulletItems = (bullets: string[]): BulletItem[] => bullets.map((value) => ({ id: crypto.randomUUID(), value }));

const toValues = (items: BulletItem[]): string[] => items.map((b) => b.value);

export const reorderBullets = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const SCRIPT_TAG_REGEX = /<a[^>]*href=["']([^"']*)["']/g;

export const BulletInput = ({
  currentBullets,
  onExecuteUpdate,
  displayNewBulletInput,
  onDisplayNewBulletInput,
  viewOnly = false
}: BulletInputProps) => {
  const [controlledBullets, setControlledBullets] = useState<BulletItem[]>(() => toBulletItems(currentBullets));

  const [newBullet, setNewBullet] = useState('');
  const [editBulletId, setEditBulletId] = useState<string | undefined>(undefined);
  const { refetch: refetchSnippetyData, fetchStatus } = useSnippetyQuery(newBullet, { enabled: false });

  const bulletRefs = useMemo(
    () => new Map(controlledBullets.map(({ id }) => [id, createRef<HTMLTextAreaElement>()])),
    [controlledBullets]
  );

  const isFetchingFromSnippety = fetchStatus === 'fetching';

  const handleNewBulletInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewBullet(e.target.value);
  };

  const handleNewBulletEnter = async (e: React.KeyboardEvent | React.FocusEvent) => {
    const key = 'key' in e ? e.key : undefined;
    const { type } = e;

    const executeUpdate = (bulletLink: string) => {
      const next = [...controlledBullets, { id: crypto.randomUUID(), value: bulletLink }];
      setControlledBullets(next);
      onExecuteUpdate(toValues(next));
      setNewBullet('');
      onDisplayNewBulletInput(false);
    };

    if (key !== 'Enter' && type !== 'blur') {
      return;
    }

    if (!isValidURL(newBullet)) {
      executeUpdate(formatBulletLink(newBullet));
      return;
    }

    try {
      const fetchedArticle = await refetchSnippetyData();

      const dataFromSnippety = fetchedArticle.data as SnippetyResponse | undefined;

      if (!dataFromSnippety) {
        executeUpdate(formatBulletLink(newBullet));

        return;
      }

      executeUpdate(formatBulletLink(newBullet, dataFromSnippety.title));
    } catch (error: unknown) {
      reportError('Error fetching article data:', error as Error);
    }
  };

  useEffect(() => {
    setControlledBullets(toBulletItems(currentBullets));
  }, [currentBullets]);

  /**
   * Updates the bullet style to match the height of the content
   */
  useLayoutEffect(() => {
    bulletRefs.forEach((_ref, id) => {
      const el = bulletRefs.get(id)?.current;
      if (el) {
        el.style.height = '0';
        el.style.height = `${el.scrollHeight}px`;
      }
    });
  }, [bulletRefs]);

  const handleRemoveBullet = (id: string) => {
    const newBullets = controlledBullets.filter((b) => b.id !== id);
    setControlledBullets(newBullets);
    onExecuteUpdate(toValues(newBullets));
  };

  const handleEditBulletInput = (
    e: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>,
    index: number,
    bulletHeadline?: boolean
  ) => {
    if (!bulletHeadline) {
      const newBullets = [...controlledBullets];
      const headline = newBullets[index].value.split('">')[1].slice(0, -4);

      if (headline) {
        newBullets[index] = {
          ...newBullets[index],
          value: formatBulletLink(e.target.value, decodeHtmlEntities(headline))
        };
      }

      setControlledBullets(newBullets);

      return;
    }

    const bulletRef = bulletRefs.get(controlledBullets[index].id);
    if (bulletRef?.current) {
      bulletRef.current.style.height = '0';
      bulletRef.current.style.height = `${bulletRef.current.scrollHeight}px`;
    }

    const newBullets = [...controlledBullets];
    const bulletLinkRegex = SCRIPT_TAG_REGEX.exec(newBullets[index].value);
    const bulletLink = newBullets[index].value.split('">')[0].slice(9);

    if (bulletLinkRegex) {
      newBullets[index] = { ...newBullets[index], value: formatBulletLink(bulletLinkRegex[1], e.target.value) };
      setControlledBullets(newBullets);
      return;
    }

    newBullets[index] = { ...newBullets[index], value: formatBulletLink(bulletLink, e.target.value) };
    setControlledBullets(newBullets);
  };

  const handleEditBulletEnter = (e: React.KeyboardEvent | React.FocusEvent) => {
    const key = 'key' in e ? e.key : undefined;
    const { type } = e;

    if (key === 'Enter' || type === 'blur') {
      setEditBulletId(undefined);
      onExecuteUpdate(toValues(controlledBullets));
    }
  };

  const handleViewBulletLink = (bulletLink: string) => {
    window.open(bulletLink, '_blank');
  };

  const handleDragEnd: DragDropContextProps['onDragEnd'] = (result) => {
    if (!result.destination) {
      return;
    }

    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }

    const items = reorderBullets(controlledBullets, result.source.index, result.destination.index);
    setControlledBullets(items);
    onExecuteUpdate(toValues(items));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="bullets-droppable">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            <List listStyle="bullet">
              {controlledBullets.map(({ id, value: bullet }, index) => {
                if (!bullet) return null;
                const link = bullet.split('">')[0].slice(9);
                const headline = bullet.split('">')[1].slice(0, -4);
                const bulletString = decodeHtmlEntities(headline);
                return (
                  <Draggable key={id} draggableId={id} index={index} isDragDisabled={viewOnly}>
                    {(provided) => (
                      <li
                        className={`st_listitem st_listitem--bullet st_m-ver-sm ${styles.bulletItemDragHandle}`}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <Wrapper className={styles.bulletItemWrapper}>
                          <Wrapper className={styles.bulletGroup}>
                            {editBulletId === id && (
                              <Input
                                data-testid="edit-bullet-input"
                                tabIndex={0}
                                value={link}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  handleEditBulletInput(e, index);
                                }}
                                onKeyDown={handleEditBulletEnter}
                                onBlur={handleEditBulletEnter}
                                disabled={viewOnly}
                              />
                            )}
                            <textarea
                              hidden={editBulletId === id && !headline.length}
                              value={bulletString}
                              placeholder="Add article link"
                              className={styles.bulletItem}
                              ref={bulletRefs.get(id)}
                              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                handleEditBulletInput(e, index, true);
                              }}
                              onBlur={() => {
                                onExecuteUpdate(toValues(controlledBullets));
                              }}
                              disabled={viewOnly}
                            />
                          </Wrapper>
                          <PortalDropdown>
                            <Button
                              onMouseDown={() => {
                                handleViewBulletLink(link);
                              }}
                              margin={{ horizontal: 'sm', top: 'sm', bottom: 'sm' }}
                              tertiary
                              icon={IconExternalLink}
                            >
                              Open Link
                            </Button>
                            <Button
                              onMouseDown={() => {
                                setEditBulletId(id);
                              }}
                              margin={{ horizontal: 'sm', top: 'sm', bottom: 'sm' }}
                              tertiary
                              icon={IconEdit}
                              disabled={viewOnly}
                            >
                              Edit Link
                            </Button>
                            <Button
                              onMouseDown={() => {
                                handleRemoveBullet(id);
                              }}
                              margin={{ horizontal: 'sm', top: 'sm', bottom: 'sm' }}
                              tertiary
                              color="lava"
                              icon={IconTrash}
                              disabled={viewOnly}
                            >
                              Remove Bullet
                            </Button>
                          </PortalDropdown>
                        </Wrapper>
                      </li>
                    )}
                  </Draggable>
                );
              })}
            </List>
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      {displayNewBulletInput && (
        <List listStyle="bullet">
          <List.Item>
            <Input
              data-testid="edit-bullet-input"
              value={newBullet}
              onChange={handleNewBulletInput}
              onKeyDown={handleNewBulletEnter}
              onBlur={handleNewBulletEnter}
              disabled={isFetchingFromSnippety || viewOnly}
              placeholder="Add article URL …"
            />
            <PortalDropdown>
              <Button
                margin={{ horizontal: 'sm', top: 'sm', bottom: 'sm' }}
                tertiary
                icon={IconTrash}
                color="lava"
                onMouseDown={() => {
                  setNewBullet('');
                  onDisplayNewBulletInput(false);
                }}
                disabled={viewOnly}
              >
                Remove Bullet
              </Button>
            </PortalDropdown>
          </List.Item>
        </List>
      )}
    </DragDropContext>
  );
};
